import { useCallback, useState, useLayoutEffect } from "react";
import { useDispatchCustomTheme, ACTIONS } from "mosaic-react/sovos-custom-theme-context";
import customThemeColors from "mosaic-react/internals/custom-themes/customThemeColors";
import LoadingPage from "../controllers/Login/LoadingPage";

/**
 * Hook to update themes from JSON data with default theme reset capability and loading state management
 * @param {boolean} loading - Loading state indicator
 * @param {boolean} success - Success state indicator
 * @returns {Object} - Object containing updateThemes function, shouldShowLoading boolean, and LoadingComponent
 */
const useUpdateThemes = (loading = false, success = false) => {
    const dispatch = useDispatchCustomTheme();
    const mosaicStorageThemeName = "mosaic_theme_config";
    const [hasThemeChanged, setHasThemeChanged] = useState(false);
    const [hasInitialTheme, setHasInitialTheme] = useState(false);

    const getDefaultThemePayload = () => ({
        colorThemes: customThemeColors || [
            {
                title: "Mosaic (default)",
                name: "Mosaic (default)",
                themeType: "dark",
                colors: {},
            },
        ],
        selectedTheme: "Mosaic (default)",
        logoUrl: "",
        logoAlt: "",
        themeType: "dark",
    });

    // Check, validate, and load theme from localStorage on initialization
    // Using useLayoutEffect to run synchronously before paint to prevent multiple reloads
    useLayoutEffect(() => {
        try {
            const storedTheme = window.localStorage.getItem(mosaicStorageThemeName);
            if (storedTheme) {
                const parsed = JSON.parse(storedTheme);

                // Try to validate existing theme
                if (
                    parsed &&
                    typeof parsed === "object" &&
                    parsed.colorThemes &&
                    Array.isArray(parsed.colorThemes)
                ) {
                    // Check if colorThemes is empty or has invalid themes
                    if (parsed.colorThemes.length === 0) {
                        window.localStorage.removeItem(mosaicStorageThemeName);
                        // Set default theme immediately
                        dispatch({
                            type: ACTIONS.SET_THEMES_STATE,
                            payload: getDefaultThemePayload(),
                        });
                        setHasInitialTheme(true);
                    } else {
                        let isValid = true;
                        for (const colorTheme of parsed.colorThemes) {
                            if (!colorTheme || !colorTheme.title) {
                                window.localStorage.removeItem(mosaicStorageThemeName);
                                isValid = false;
                                break;
                            }
                        }
                        // If valid, dispatch the stored theme immediately
                        if (isValid) {
                            dispatch({ type: ACTIONS.SET_THEMES_STATE, payload: parsed });
                            setHasInitialTheme(true);
                        } else {
                            // Set default theme immediately
                            dispatch({
                                type: ACTIONS.SET_THEMES_STATE,
                                payload: getDefaultThemePayload(),
                            });
                            setHasInitialTheme(true);
                        }
                    }
                }
            } else {
                // No stored theme, set default theme immediately
                dispatch({
                    type: ACTIONS.SET_THEMES_STATE,
                    payload: getDefaultThemePayload(),
                });
                setHasInitialTheme(true);
            }
        } catch (error) {
            window.localStorage.removeItem(mosaicStorageThemeName);
            // Set default theme immediately on error
            dispatch({
                type: ACTIONS.SET_THEMES_STATE,
                payload: getDefaultThemePayload(),
            });
            setHasInitialTheme(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetToDefaultTheme = useCallback(
        (themes = null, triggerLoading = false) => {
            try {
                // Use provided themes or fallback to default
                const defaultThemeState = themes
                    ? {
                          colorThemes: themes,
                          selectedTheme: "Mosaic (default)",
                          logoUrl: "",
                          logoAlt: "",
                          themeType: "dark",
                      }
                    : getDefaultThemePayload();

                dispatch({
                    type: ACTIONS.SET_THEMES_STATE,
                    payload: defaultThemeState,
                });
                // Only trigger loading if explicitly requested
                if (triggerLoading) {
                    setHasThemeChanged(true);
                }
            } catch (error) {
                console.error("Error resetting to default theme:", error);
            }
        },
        [dispatch],
    );

    const validateAndSanitizeTheme = (theme) => {
        if (!theme || typeof theme !== "object") {
            return null;
        }

        // Create a safe theme structure
        const safeTheme = {
            colorThemes: [],
            selectedTheme: theme.selectedTheme || "",
            logoUrl: theme.logoUrl || "",
            logoAlt: theme.logoAlt || "",
            themeType: theme.themeType || "dark",
        };

        // Validate and sanitize colorThemes
        if (theme.colorThemes && Array.isArray(theme.colorThemes) && theme?.colorThemes?.length) {
            for (const colorTheme of theme.colorThemes) {
                if (!colorTheme || typeof colorTheme !== "object") {
                    continue;
                }

                if (!colorTheme.title || typeof colorTheme.title !== "string") {
                    colorTheme.title = colorTheme.name || "Unnamed Theme";
                }

                // Ensure required properties exist
                const sanitizedColorTheme = {
                    title: colorTheme.title || "Unnamed Theme",
                    name: colorTheme.name || colorTheme.title || "Unnamed Theme",
                    themeType: colorTheme.themeType || "dark",
                    colors: colorTheme.colors || {},
                    ...colorTheme,
                };

                safeTheme.colorThemes.push(sanitizedColorTheme);
            }
        }

        // Ensure we always have at least one theme to prevent reducer crashes
        if (safeTheme.colorThemes.length === 0) {
            safeTheme.colorThemes.push({
                title: "Mosaic (default)",
                name: "Mosaic (default)",
                themeType: "dark",
                colors: {},
            });
            safeTheme.selectedTheme = "Mosaic (default)";
        }

        // Ensure selectedTheme exists in colorThemes
        const hasSelectedTheme = safeTheme.colorThemes.some(
            (t) => t.title === safeTheme.selectedTheme,
        );
        if (!hasSelectedTheme && safeTheme.colorThemes.length > 0) {
            safeTheme.selectedTheme = safeTheme.colorThemes[0].title;
        }

        return safeTheme;
    };

    const updateThemes = useCallback(
        (themeJson) => {
            try {
                const mosaicLocalStorageTheme = window.localStorage.getItem(mosaicStorageThemeName);

                const parsedLocalTheme = mosaicLocalStorageTheme
                    ? JSON.parse(mosaicLocalStorageTheme)
                    : null;
                const hasLocalTheme = parsedLocalTheme && Object.keys(parsedLocalTheme).length > 0;
                const hasNewTheme = themeJson && Object.keys(themeJson).length > 0;

                // Validate and sanitize theme structure before applying
                let sanitizedTheme = null;
                if (hasNewTheme) {
                    sanitizedTheme = validateAndSanitizeTheme(themeJson);

                    if (!sanitizedTheme) {
                        setHasThemeChanged(true);
                        resetToDefaultTheme(customThemeColors);
                        return;
                    }
                }

                if (
                    hasLocalTheme &&
                    hasNewTheme &&
                    mosaicLocalStorageTheme === JSON.stringify(sanitizedTheme)
                ) {
                    // Theme is already the same, just ensure it's dispatched but don't show loading
                    dispatch({ type: ACTIONS.SET_THEMES_STATE, payload: sanitizedTheme });
                    return;
                }

                if (hasNewTheme && sanitizedTheme) {
                    // Only update localStorage and dispatch, don't set hasThemeChanged
                    // The theme change will be applied without showing loading screen
                    dispatch({ type: ACTIONS.SET_THEMES_STATE, payload: sanitizedTheme });
                    window.localStorage.setItem(
                        mosaicStorageThemeName,
                        JSON.stringify(sanitizedTheme),
                    );
                    // Don't set hasThemeChanged to avoid flash/reload
                    return;
                }

                if (hasLocalTheme) {
                    window.localStorage.removeItem(mosaicStorageThemeName);
                }
                resetToDefaultTheme(customThemeColors);
            } catch (error) {
                // Fallback to default theme on any error (don't show loading)
                if (window.localStorage.getItem(mosaicStorageThemeName)) {
                    window.localStorage.removeItem(mosaicStorageThemeName);
                }
                resetToDefaultTheme(customThemeColors);
            }
        },
        [dispatch, resetToDefaultTheme, mosaicStorageThemeName],
    );

    const shouldShowLoading = hasThemeChanged || (loading && !success && !hasInitialTheme);

    const LoadingComponent = shouldShowLoading ? <LoadingPage /> : null;

    return {
        updateThemes,
        LoadingComponent,
        resetToDefaultTheme,
    };
};

export default useUpdateThemes;
